import type { ReviewFacts, ReviewField, TechnicalFinding } from "./technical-rules.js";

/**
 * ReportValidationEngine — regras OBJETIVAS, executadas ANTES da IA.
 *
 * A IA não substitui código determinístico: campos obrigatórios, datas,
 * períodos e as pendências que o próprio módulo já calcula (o mesmo
 * validador que bloqueia o envio ao PLH) entram aqui como findings de
 * VALIDAÇÃO. Nenhuma regra de módulo é reescrita — o adaptador entrega as
 * pendências do validador oficial do módulo.
 */
export interface DeterministicInput {
  readonly fields: readonly ReviewField[];
  readonly facts: ReviewFacts;
  /** Pendências do validador oficial do módulo (ex.: validateMeItems). */
  readonly modulePendencies: readonly string[];
  readonly today: string;
}

const DAY = /^\d{4}-\d{2}-\d{2}$/;

export function runDeterministicValidation(input: DeterministicInput): TechnicalFinding[] {
  const out: TechnicalFinding[] = [];
  for (const message of input.modulePendencies) {
    out.push({ ruleId: "MODULE_VALIDATION", category: "VALIDACAO", severity: "TECNICO", fieldPath: "", originalText: "", suggestedText: "", explanation: message, justifiable: false });
  }
  for (const d of input.facts.dates) {
    if (!d.value) continue;
    if (!DAY.test(d.value.slice(0, 10))) {
      out.push({ ruleId: "DATE_FORMAT", category: "VALIDACAO", severity: "TECNICO", fieldPath: "", originalText: d.value, suggestedText: "", explanation: `${d.label}: data em formato inválido.`, justifiable: false });
    } else if (d.value.slice(0, 10) > input.today) {
      out.push({ ruleId: "DATE_FUTURE", category: "VALIDACAO", severity: "TECNICO", fieldPath: "", originalText: d.value, suggestedText: "", explanation: `${d.label} (${d.value.slice(0, 10)}) está no futuro.`, justifiable: true });
    }
  }
  for (const p of input.facts.periods) {
    if (p.start && p.end && p.start.slice(0, 10) > p.end.slice(0, 10)) {
      out.push({ ruleId: "PERIOD_ORDER", category: "VALIDACAO", severity: "TECNICO", fieldPath: "", originalText: `${p.start} → ${p.end}`, suggestedText: "", explanation: `${p.label}: a data inicial é posterior à final.`, justifiable: false });
    }
  }
  for (const m of input.facts.measurements) {
    if (typeof m.value === "number" && (!Number.isFinite(m.value) || m.value <= 0)) {
      out.push({ ruleId: "MEASUREMENT_INVALID", category: "VALIDACAO", severity: "TECNICO", fieldPath: "", originalText: String(m.value), suggestedText: "", explanation: `${m.label}: espessura inválida.`, justifiable: false });
    }
  }
  return out;
}

// ═══════════════════════════════════════════════════════════ hash do conteúdo

/** JSON com chaves ordenadas — o mesmo conteúdo sempre dá o mesmo texto. */
export function stableStringify(value: unknown): string {
  if (value === null || typeof value !== "object") return JSON.stringify(value ?? null);
  if (Array.isArray(value)) return `[${value.map(stableStringify).join(",")}]`;
  const obj = value as Record<string, unknown>;
  return `{${Object.keys(obj).sort().filter(k => obj[k] !== undefined).map(k => `${JSON.stringify(k)}:${stableStringify(obj[k])}`).join(",")}}`;
}

/**
 * reviewedContentHash — FNV-1a de 64 bits sobre os campos revisados + fatos.
 * Serve para detectar mudança (não é segurança): relatório alterado depois
 * da revisão ⇒ revisão DESATUALIZADA ⇒ nova revisão antes de enviar.
 */
export function contentHash(fields: readonly ReviewField[], facts: ReviewFacts): string {
  const payload = stableStringify({ fields: fields.map(f => ({ p: f.fieldPath, t: f.text })), facts });
  let h = 0xcbf29ce484222325n;
  const prime = 0x100000001b3n;
  for (let i = 0; i < payload.length; i++) {
    h ^= BigInt(payload.charCodeAt(i));
    h = (h * prime) & 0xffffffffffffffffn;
  }
  return h.toString(16).padStart(16, "0");
}
